/**
 * useDashboardFire — серверные fire-события для NodeOrbit + сетевой
 * тикер (общая активность платформы).
 *
 * Подписывается на SSE `/api/luna/dashboard/stream` и слушает:
 *   - `event: fire`    — личные node-выстрелы (для NodeOrbit)
 *   - `event: network` — реальные клеймы юзеров (для LiveFeedTicker)
 *
 * Все клиенты видят одинаковый поток (сервер генерирует один
 * глобальный стрим), поэтому два юзера, сравнивая скриншоты,
 * увидят те же цифры.
 */
import { useEffect, useRef, useState } from 'react';

export interface FireEvent {
  nodeIdx: number;   // 0–6
  amount: number;    // 0.05–2.50 USDT
  ts: number;
}

export interface NetworkEvent {
  short: string;     // ST | TM | VPN | PF | CS | AI | AD
  amount: number;    // ≥ 0.50 USD₮ (фильтр на бэке)
  ts: number;
  anonId: string;    // u_xxx***xx
}

const STREAM_URL = '/api/luna/dashboard/stream';

export function useDashboardFire(): FireEvent | null {
  const [fire, setFire] = useState<FireEvent | null>(null);
  const sourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    const es = new EventSource(STREAM_URL);
    sourceRef.current = es;

    es.addEventListener('fire', (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data) as FireEvent;
        setFire(data);
      } catch { /* malformed — skip */ }
    });

    es.onerror = () => {
      // Браузер автоматически переподключается для SSE
    };

    return () => {
      es.close();
      sourceRef.current = null;
    };
  }, []);

  return fire;
}

/**
 * Подписка на сетевые события (реальная активность платформы).
 * Возвращает последнее событие. Может быть null долгое время —
 * сервер пушит только клеймы ≥ $0.50 раз в 15 сек.
 */
export function useNetworkEvent(): NetworkEvent | null {
  const [evt, setEvt] = useState<NetworkEvent | null>(null);

  useEffect(() => {
    const es = new EventSource(STREAM_URL);

    es.addEventListener('network', (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data) as NetworkEvent;
        setEvt(data);
      } catch { /* malformed — skip */ }
    });

    return () => { es.close(); };
  }, []);

  return evt;
}
